import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, FormControl, TextField } from '@mui/material';
import { AccessDataContext } from 'contextProviders/AccessDataContext';
import { OverlayContext } from 'contextProviders/OverlayContext';
import { ProfileApis } from 'hooks/apis/ProfileApis';
import { useValidation } from 'hooks/UseValidation';
import { IChangePasswordValues } from 'interfaces/Profile/IChangePasswordValues';

const ChangePassword = () => {
  const { user } = useContext(AccessDataContext);
  const { setShowLoadingOverlay } = useContext(OverlayContext);
  const { validatePassword } = useValidation();
  const { postChangePassword } = ProfileApis();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState<string>(null);
  const [newPassword, setNewPassword] = useState<string>(null);
  const [confirmPassword, setConfirmPassword] = useState<string>(null);
  const [passwordValid, setPasswordValid] = useState(true);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [changePasswordSuccessful, setChangePasswordSuccessful] = useState(false);
  const [changePasswordErrors, setChangePasswordErrors] = useState<string>(null);

  const changePassword = useCallback(
    (e) => {
      e.preventDefault();

      const checkPassword = validatePassword(newPassword);
      setPasswordValid(checkPassword);
      if (!passwordsMatch || !checkPassword) {
        return;
      }

      setShowLoadingOverlay(true);

      const data: IChangePasswordValues = {
        CurrentPassword: currentPassword,
        NewPassword: newPassword,
        ConfirmPassword: confirmPassword,
      };

      postChangePassword(user.Id, data).then((returnVal) => {
        if (returnVal && returnVal.message) {
          setChangePasswordSuccessful(true);
        } else if (returnVal && returnVal.errorMessage) {
          setChangePasswordErrors(returnVal.errorMessage);
        }
        setShowLoadingOverlay(false);
      });
    },
    [currentPassword, newPassword, confirmPassword, passwordsMatch, user, validatePassword, postChangePassword, setShowLoadingOverlay]
  );

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setCurrentPassword(null);
    setNewPassword(null);
    setConfirmPassword(null);
    setPasswordValid(true);
    setPasswordsMatch(true);
    setChangePasswordSuccessful(false);
    setChangePasswordErrors(null);
  }, []);

  useEffect(() => {
    setPasswordsMatch(newPassword === confirmPassword);
  }, [newPassword, confirmPassword, setPasswordsMatch]);

  useEffect(() => {
    if (!passwordValid) {
      setPasswordValid(validatePassword(newPassword));
    }
  }, [newPassword, validatePassword, passwordValid]);

  return (
    <>
      <Button onClick={() => setDialogOpen(true)}>Change Password</Button>
      <Dialog open={dialogOpen} onClose={() => closeDialog()}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {!changePasswordSuccessful ? (
            <Box className="ChangePassword">
              <form onSubmit={changePassword}>
                <FormControl className="formElement" fullWidth>
                  <TextField
                    size="small"
                    label="Current Password"
                    type="password"
                    name="currentPassword"
                    onChange={(item) => setCurrentPassword(item.target.value)}
                    required
                  />
                </FormControl>

                <FormControl className="formElement" fullWidth>
                  <TextField
                    size="small"
                    label="Password"
                    type="password"
                    name="newPassword"
                    error={!passwordValid}
                    helperText={
                      !passwordValid ? 'Minimum six characters, at least one uppercase letter, one lowercase letter and one number' : ''
                    }
                    onChange={(item) => setNewPassword(item.target.value)}
                    required
                  />
                </FormControl>

                <FormControl className="formElement" fullWidth>
                  <TextField
                    size="small"
                    label="Confirm Password"
                    type="password"
                    name="confirmPassword"
                    error={!passwordsMatch}
                    helperText={!passwordsMatch ? 'Passwords do not match' : ''}
                    onChange={(item) => setConfirmPassword(item.target.value)}
                    required
                  />
                </FormControl>

                <Button type="submit" className="siteBtn">
                  Submit
                </Button>

                {changePasswordErrors ? <Box className="errorMessage">{changePasswordErrors}</Box> : null}
              </form>
            </Box>
          ) : (
            <Box>Password updated successfully</Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChangePassword;
